import React, { useState } from 'react';
import { X, ArrowsClockwise, Sparkle, CheckCircle } from '@phosphor-icons/react';

export default function SwapChipPopover({ api, swap, onClose, onSwapped }) {
  const [mode, setMode] = useState('similar');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);

  const MODES = [
    { id: 'similar', label: 'Similar', hint: 'Same style, different dish', color: '#3B82F6' },
    { id: 'surprise', label: 'Surprise me', hint: 'Something unexpected', color: '#A855F7' },
    { id: 'lighter', label: 'Lighter', hint: 'Less oil, healthier', color: '#10B981' },
    { id: 'spicier', label: 'Spicier', hint: 'More heat / flavour', color: '#DC2626' },
    { id: 'kid_friendly', label: 'Kid-friendly', hint: 'Mild, familiar', color: '#F59E0B' },
    { id: 'healthier', label: 'High-protein', hint: 'Less carbs, more protein', color: '#06B6D4' },
  ];

  const handleSwap = async () => {
    setBusy(true);
    setErr('');
    try {
      const res = await api.post(`/meals/plans/${swap.plan_id}/replace-chip`, {
        slot: swap.slot,
        index: swap.index,
        mode,
        reason: reason.trim(),
      });
      setResult(res.data);
      if (onSwapped) onSwapped();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Swap failed. Try a different mode.');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="swap-chip-modal"
      style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: '#1A1612', border: '1px solid rgba(245,158,11,0.25)' }}>
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.15), rgba(245,158,11,0.05))', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <ArrowsClockwise size={18} weight="bold" className="text-amber-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider font-bold">Swap dish</p>
              <p className="text-sm font-bold text-white truncate">{swap.dish}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="swap-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!result && (
            <>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-2 block">How should the AI swap it?</label>
                <div className="grid grid-cols-2 gap-2">
                  {MODES.map(m => (
                    <button key={m.id} onClick={() => setMode(m.id)}
                      data-testid={`swap-mode-${m.id}`}
                      className={`text-left p-2.5 rounded-xl border transition-colors ${mode === m.id ? 'border-amber-500/60 bg-amber-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                      <p className="text-[12px] font-bold text-white" style={{ color: mode === m.id ? m.color : undefined }}>{m.label}</p>
                      <p className="text-[10px] text-neutral-500 mt-0.5">{m.hint}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500 mb-1 block">Reason (optional)</label>
                <input type="text" value={reason} onChange={e => setReason(e.target.value)}
                  placeholder="e.g. had it yesterday, too oily, kids didn&apos;t like"
                  data-testid="swap-reason-input"
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-sm text-white placeholder:text-neutral-600 focus:border-amber-500 outline-none" />
                <p className="text-[10px] text-neutral-500 mt-1">AI will remember rejections. After 3 swaps of the same dish, it auto-marks it as disliked.</p>
              </div>

              {err && (
                <div className="rounded-lg p-2.5 text-[11px] text-red-300 max-h-40 overflow-y-auto" style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)' }} data-testid="swap-error">
                  {err}
                </div>
              )}

              <button onClick={handleSwap} disabled={busy} data-testid="swap-go-btn"
                className="w-full py-2.5 rounded-xl bg-amber-500 text-black text-sm font-bold flex items-center justify-center gap-2 hover:bg-amber-400 disabled:opacity-50">
                {busy ? (
                  <>
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    AI is thinking…
                  </>
                ) : (
                  <>
                    <Sparkle size={15} weight="fill" />
                    Swap with AI
                  </>
                )}
              </button>
            </>
          )}

          {result && (
            <div className="space-y-4" data-testid="swap-result">
              <div className="rounded-xl p-4 text-center"
                style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.1), rgba(16,185,129,0.04))', border: '1px solid rgba(16,185,129,0.25)' }}>
                <CheckCircle size={24} weight="fill" className="text-emerald-400 mx-auto mb-2" />
                <p className="text-[10px] text-neutral-500 uppercase tracking-wider font-bold">Swapped</p>
                <p className="text-sm text-neutral-400 line-through">{result.replaced?.from}</p>
                <p className="text-xs text-neutral-500 my-1">↓</p>
                <p className="text-base font-bold text-emerald-300">{result.replaced?.to}</p>
              </div>

              {result.rationale && (
                <div className="rounded-lg p-3 flex items-start gap-2" data-testid="swap-rationale"
                  style={{ background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.25)' }}>
                  <Sparkle size={14} weight="fill" className="text-blue-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[9px] uppercase tracking-wider font-bold text-blue-300 mb-0.5">Why this swap</p>
                    <p className="text-[12px] text-neutral-200 leading-relaxed">{result.rationale}</p>
                  </div>
                </div>
              )}

              {result.auto_disliked && (
                <div className="rounded-lg p-3 text-[11px]" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <p className="text-amber-300 font-bold mb-0.5">📝 Saved to disliked dishes</p>
                  <p className="text-neutral-400">You&apos;ve swapped <b>{result.replaced?.from}</b> 3+ times. AI Chef will avoid suggesting it from now on.</p>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={onClose} data-testid="swap-done-btn"
                  className="flex-1 py-2.5 rounded-xl bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25 text-sm font-bold">
                  Done
                </button>
                <button onClick={() => { setResult(null); setReason(''); }}
                  data-testid="swap-again-btn"
                  className="px-4 py-2.5 rounded-xl bg-white/5 text-neutral-300 border border-white/10 hover:bg-white/10 text-sm font-bold">
                  Swap again
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
